import {
  Box,
  Divider,
  Heading,
  Link,
  ListItem,
  Text,
  UnorderedList,
  VStack
} from '@chakra-ui/react'
import React from 'react'

export const TermsTab: React.FC = () => {
  return (
    <Box p={4} maxHeight="460px" overflowY="auto">
      <VStack align="stretch" spacing={4}>
        {/* Header */}
        <Heading as="h3" size="md">
          🎁 Blueprint Referral Program
        </Heading>
        <Text fontStyle="italic">Last updated: May 5, 2025</Text>
        <Text>
          Welcome! Earn free months and gift card rewards by referring your
          colleagues to Blueprint. Below is everything you need to know—clear,
          helpful, and legally solid.
        </Text>

        <Divider />

        {/* How It Works */}
        <Heading as="h3" size="md">
          💡 How It Works
        </Heading>

        <Box>
          <Text fontWeight="bold">Step 1: Get Your Link</Text>
          <Text pl={4}>
            Copy or email your unique referral link from your Blueprint account.
          </Text>
        </Box>

        <Box>
          <Text fontWeight="bold">Step 2: Share It</Text>
          <Text pl={4}>Send your link via email, text, or social media.</Text>
        </Box>

        <Box>
          <Text fontWeight="bold">Step 3: They Sign Up</Text>
          <Text pl={4}>
            Your colleagues and/or followers create a free account, discover the
            magic of Blueprint and convert their account to any paid plan.
          </Text>
        </Box>

        <Box>
          <Text fontWeight="bold">Step 4: You Both Win</Text>
          <Text pl={4}>
            They get{' '}
            <Text as="span" fontWeight="bold">
              1 month of Blueprint Standard or Plus for free
            </Text>
            . You earn free months of Blueprint Standard or Plus and bonus
            rewards when they become a paid subscriber.
          </Text>
        </Box>

        <Divider />

        {/* What "Month Free" Means */}
        <Heading as="h3" size="md">
          🧾 What "Month Free" Means
        </Heading>

        <Text>
          A "month free" is a{' '}
          <Text as="span" fontWeight="bold">
            dollar credit
          </Text>
          , not unlimited usage. It's based on the plan and session tier you're
          on when the reward is applied.
        </Text>

        <UnorderedList spacing={2} pl={4}>
          <ListItem>
            Standard Plan (120 sessions):{' '}
            <Text as="span" fontWeight="bold">
              $119 credit
            </Text>
          </ListItem>
          <ListItem>
            Plus Plan (120 sessions):{' '}
            <Text as="span" fontWeight="bold">
              $155 credit
            </Text>
          </ListItem>
          <ListItem>
            Lower session tiers: You get a credit equal to your monthly plan
            cost (e.g., $29 for Standard/30)
          </ListItem>
          <ListItem>
            Higher tiers: You get the credit above, and pay only the difference
          </ListItem>
        </UnorderedList>

        <Text fontWeight="bold">
          All credits are capped at the value of the 120-session tier.
        </Text>
        <Text>
          Credits apply automatically before your next billing cycle. Plan
          changes are allowed, and credits still apply.
        </Text>

        <Link color="blue.500" href="https://blueprint.ai/pricing">
          🔗 View all pricing tiers →
        </Link>

        <Divider />

        {/* Rewards & Tiers */}
        <Heading as="h3" size="md">
          🏆 Rewards & Tiers
        </Heading>

        <Text>
          Each time someone you refer becomes a paying Blueprint user, you
          unlock more rewards.
        </Text>
        <Text>
          🎯 See full breakdown in your{' '}
          <Text as="span" fontWeight="bold">
            Reward Tiers tab
          </Text>{' '}
          in your referral dashboard.
        </Text>

        <Heading as="h4" size="sm">
          🎁 Amazon Gift Cards
        </Heading>

        <Text>
          Instead of shipping physical items, we'll email you an Amazon gift
          card when you hit certain milestones:
        </Text>

        <UnorderedList spacing={2} pl={4}>
          <ListItem>$100 (tea set equivalent)</ListItem>
          <ListItem>$200 (audio gift equivalent)</ListItem>
          <ListItem>$500 (device gift equivalent)</ListItem>
        </UnorderedList>

        <Text>
          Gift cards are sent to the email on your Blueprint account once you
          qualify.
        </Text>

        <Heading as="h4" size="sm">
          🔓 Blueprint Free for Life
        </Heading>

        <Text>
          Refer{' '}
          <Text as="span" fontWeight="bold">
            10 paying customers
          </Text>{' '}
          and your Blueprint account becomes{' '}
          <Text as="span" fontWeight="bold">
            FREE FOR LIFE
          </Text>
          .
        </Text>
        <Text>
          Yes, really—for life. As long as your account remains in good
          standing, you'll never pay again.
        </Text>
        <Text>
          This reward is{' '}
          <Text as="span" fontWeight="bold">
            non-transferable
          </Text>{' '}
          and applies only to your original Blueprint account.
        </Text>

        <Divider />

        {/* Tracking & Notifications */}
        <Heading as="h3" size="md">
          📊 Tracking & Notifications
        </Heading>

        <UnorderedList spacing={2} pl={4}>
          <ListItem>
            🔔{' '}
            <Text as="span" fontWeight="bold">
              In-app
            </Text>
            : See real-time updates (clicks, signups, paid conversions)
          </ListItem>
          <ListItem>
            📧{' '}
            <Text as="span" fontWeight="bold">
              Email
            </Text>
            : Get alerts when you earn rewards
          </ListItem>
        </UnorderedList>

        <Divider />

        {/* Promotions */}
        <Heading as="h3" size="md">
          🎉 Promotions
        </Heading>

        <Text>
          Occasionally, we'll offer bonus referral perks—extra credit,
          limited-time challenges, and more.
        </Text>
        <Text>Watch your email or Blueprint dashboard for new offers.</Text>

        <Divider />

        {/* Terms & Conditions */}
        <Heading as="h3" size="md">
          ⚖️ Terms & Conditions
        </Heading>

        <Text fontWeight="bold" mb={2}>
          Eligibility
        </Text>
        <UnorderedList spacing={2} pl={4} mb={4}>
          <ListItem>
            Open to Blueprint paying customers in good standing, 18+ only
          </ListItem>
          <ListItem>
            Referred users must be new (no prior Blueprint account)
          </ListItem>
        </UnorderedList>

        <Text fontWeight="bold" mb={2}>
          Reward Conditions
        </Text>
        <VStack align="stretch" spacing={3} pl={4}>
          <Box>
            <Text fontWeight="medium">🧾 Paid users only</Text>
            <Text>
              Free account signups don't count—only referrals who become paying
              customers qualify.
            </Text>
          </Box>

          <Box>
            <Text fontWeight="medium">💸 No cash value</Text>
            <Text>
              Credits are non-transferable, have no cash value, and expire if
              not used.
            </Text>
          </Box>

          <Box>
            <Text fontWeight="medium">🚫 Refunds void rewards</Text>
            <Text>
              If a referred user cancels within their refund window or fails to
              pay, the referral is void.
            </Text>
          </Box>

          <Box>
            <Text fontWeight="medium">🕒 30-day attribution window</Text>
            <Text>
              Referral credit is awarded to the last referral link used within
              30 days. Signups outside this period or through other sources
              won't count.
            </Text>
          </Box>

          <Box>
            <Text fontWeight="medium">🏢 Business referral restrictions</Text>
            <Text>
              Blueprint may reject referrals from businesses that are
              affiliates, resellers, or owned/operated by the referring party.
            </Text>
          </Box>

          <Box>
            <Text fontWeight="medium">📦 Reward fulfillment timing</Text>
            <Text>
              Rewards (credits or gift cards) are fulfilled within 10 business
              days of a qualifying paid referral, unless otherwise stated.
            </Text>
          </Box>

          <Box>
            <Text fontWeight="medium">✅ Account must be in good standing</Text>
            <Text>
              Your Blueprint account must be active and in good standing to
              receive any rewards.
            </Text>
          </Box>

          <Box>
            <Text fontWeight="medium">🔗 Referral link required</Text>
            <Text>
              Referrals must sign up using your unique referral link. We can't
              retroactively apply rewards.
            </Text>
          </Box>

          <Box>
            <Text fontWeight="medium">
              🎉 Affiliate program eligibility after 10
            </Text>
            <Text>
              If you refer more than 10 paying users, you won't receive
              additional referral rewards—but you'll automatically be invited to
              join our{' '}
              <Text as="span" fontWeight="bold">
                Affiliate Program
              </Text>
              , which offers{' '}
              <Text as="span" fontWeight="bold">
                cash payouts
              </Text>
              . To participate, you must register separately.
            </Text>
          </Box>

          <Box>
            <Text fontWeight="medium">🚨 No funny business</Text>
            <Text>
              No spam, fake accounts, self-referrals, or misleading promotions.
              Abuse may lead to disqualification.
            </Text>
          </Box>

          <Box>
            <Text fontWeight="medium">🛑 Abuse protection</Text>
            <Text>
              We reserve the right to revoke rewards or disqualify participants
              if abuse is suspected.
            </Text>
          </Box>
        </VStack>

        <Text fontWeight="bold" mb={2}>
          Program Changes
        </Text>
        <UnorderedList spacing={2} pl={4} mb={4}>
          <ListItem>
            We may update or end the program at any time. You'll always find the
            latest version here.
          </ListItem>
        </UnorderedList>

        <Text fontWeight="bold" mb={2}>
          Limitation of Liability
        </Text>
        <UnorderedList spacing={2} pl={4} mb={4}>
          <ListItem>
            We're not liable for delays in credit delivery, technical issues, or
            gift card email errors.
          </ListItem>
          <ListItem>
            We may substitute rewards of equal or greater value if needed.
          </ListItem>
        </UnorderedList>

        <Text fontWeight="bold" mb={2}>
          Governing Law
        </Text>
        <UnorderedList spacing={2} pl={4} mb={4}>
          <ListItem>This program is governed by Delaware law.</ListItem>
        </UnorderedList>

        <Divider />

        {/* FAQs */}
        <Heading as="h3" size="md">
          ❓ FAQs
        </Heading>

        <VStack align="stretch" spacing={3}>
          <Box>
            <Text fontWeight="bold">How do I get my referral link?</Text>
            <Text>Go to your account → Referral tab → Click "Copy Link."</Text>
          </Box>

          <Box>
            <Text fontWeight="bold">
              What if my colleague signs up but doesn't pay?
            </Text>
            <Text>
              You'll see their signup, but rewards are earned only when they
              choose a paid plan.
            </Text>
          </Box>

          <Box>
            <Text fontWeight="bold">Where can I track progress?</Text>
            <Text>
              Your referral dashboard shows link clicks, free accounts created,
              free accounts being actively used (as measured by real sessions
              and notes generated), and paid conversions.
            </Text>
          </Box>

          <Box>
            <Text fontWeight="bold">
              Do free months and gift card rewards stack?
            </Text>
            <Text>Absolutely. Each referral builds toward bigger rewards.</Text>
          </Box>
        </VStack>
      </VStack>
    </Box>
  )
}

export default TermsTab
